/**
 * Auth Feature Constants
 *
 * Centralized constants for authentication-related values.
 * Eliminates magic strings and provides type safety.
 */

export const AUTH_CONSTANTS = {
  /** OAuth grant types */
  GRANT_TYPE: {
    PASSWORD: "password",
    REFRESH_TOKEN: "refresh_token",
  },

  /** OAuth scopes */
  SCOPE: {
    REPRESENTATIVE: "representative",
    ELU: "elu",
  },

  /** Error codes returned by the API */
  ERROR_CODES: {
    /** User has attempted too many 2FA verifications */
    TOO_MANY_2FA_ATTEMPTS: "too_many_2fa_attempts",
    /** User account is missing a registered cell phone number */
    MISSING_CELL_PHONE: "missing_cell_phone_number",
    /** Generic/unknown error */
    UNKNOWN_ERROR: "unknown_error",
    /** Invalid OTP code */
    INVALID_OTP: "invalid_otp",
    /** Invalid challenge token */
    INVALID_CHALLENGE: "invalid_challenge",
    /** Unauthorized access */
    UNAUTHORIZED: "unauthorized",
    /** Impersonation failed */
    IMPERSONATION_FAILED: "impersonation_failed",
    /** Plan fetch error */
    PLAN_FETCH_ERROR: "plan_fetch_error",
  },

  /** 2FA configuration */
  TWO_FA: {
    /** Required OTP code length */
    OTP_LENGTH: 6,
    /** Maximum 2FA verification attempts before lockout */
    MAX_ATTEMPTS: 5,
  },

  /** Toast notification durations in milliseconds */
  TOAST_DURATION_MS: 10000,

  /** Local storage keys */
  STORAGE_KEYS: {
    ACCESS_TOKEN: "accessToken",
    DEVICE_ID: "deviceId",
    EXPIRES_AT: "expiresAt",
    SIDEBAR_EXPANDED: "sidebarExpanded",
    SIDEBAR_COLLAPSED_ITEMS: "sidebarCollapsedItems",
  },
} as const;

/**
 * Toast messages used in the auth flow
 */
export const AUTH_TOASTS = {
  LOGIN_SUCCESS: {
    type: "success" as const,
    info: "Connexion réussie! 🚀",
    description: "Vous êtes maintenant connecté à votre compte.",
  },
  PHONE_REQUIRED: {
    type: "info" as const,
    info: "Numéro de téléphone manquant",
    description: "Veuillez enregistrer votre numéro de téléphone pour continuer.",
  },
  CODE_SENT: {
    type: "info" as const,
    info: "Un code vous a été envoyé sur votre téléphone",
    description: "Veuillez le saisir pour vous connecter",
  },
  PHONE_REGISTERED: {
    type: "info" as const,
    info: "Numéro de téléphone enregistré avec succès !",
    description: "Vérifier votre appareil pour continuer.",
  },
  IMPERSONATION_SUCCESS: {
    type: "success" as const,
    info: "Impersonation réussie ! 😎",
    description: "Vous êtes maintenant connecté en tant que représentant de l'organisation.",
  },
} as const;

export type AuthToast = (typeof AUTH_TOASTS)[keyof typeof AUTH_TOASTS];
